import { useCallback, useEffect, useMemo } from 'react';
import { InView } from 'react-intersection-observer';

import { ProgressCircle, Text } from '@spotify-internal/encore-web';
import { List } from '@spotify-internal/encore-web/next';
import {
  CREATE_TIME_FILTER,
  DialogComposite,
  FilterBar,
  FiltersProvider,
  UPDATE_TIME_FILTER,
  useFilters,
} from '@spotify/confidence-core-react';
import {
  CREATOR_FILTER,
  OWNER_FILTER,
} from '@spotify/plugin-confidence-iam-react';

import {
  type ResourceType,
  useResourceSearch,
} from '../../hooks/useResourceSearch';
import { ResourceListItem } from './ResourceListItem';
import { ALL_TYPES } from './resourceTypeConfig';
import { RESOURCE_TYPE_FILTER } from './resourceTypeFilter';

import styles from './SearchResourcesDialog.module.css';

const DIALOG_FILTERS = [
  RESOURCE_TYPE_FILTER,
  OWNER_FILTER,
  CREATOR_FILTER,
  CREATE_TIME_FILTER,
  UPDATE_TIME_FILTER,
];

interface SearchResourcesDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (resource: { id: string; label: string }) => void;
}

const SearchResourcesDialogInner = ({
  open,
  onClose,
  onSelect,
}: SearchResourcesDialogProps) => {
  const { filterString, activeFilters, resetFilters } = useFilters();

  const activeTypes = useMemo(() => {
    const typeFilter = activeFilters.find(f => f.id === 'resourceType');
    const selected = typeFilter?.value as ResourceType[] | undefined;
    if (!selected || selected.length === 0) return ALL_TYPES;
    return selected;
  }, [activeFilters]);

  const {
    results,
    isEmpty,
    isLoading,
    isRefetching,
    hasNextPage,
    loadMore,
    isLoadingMore,
  } = useResourceSearch({
    filterString,
    activeTypes,
    skip: !open,
  });

  useEffect(() => {
    if (!open) {
      resetFilters();
    }
  }, [open, resetFilters]);

  const handleSelect = useCallback(
    (item: { name: string; displayName: string }) => {
      onSelect({ id: item.name, label: item.displayName });
    },
    [onSelect],
  );

  return (
    <DialogComposite.Root open={open} onClose={onClose} width="sm">
      <DialogComposite.Content>
        <DialogComposite.Header>
          <DialogComposite.Title>Search resources</DialogComposite.Title>
        </DialogComposite.Header>
        <DialogComposite.Body>
          <div className={styles.content}>
            <div className={styles.filterBarWrap}>
              <FilterBar />
            </div>
            <div
              className={styles.list}
              data-refetching={isRefetching || undefined}
            >
              {isLoading && results.length === 0 && (
                <div className={styles.centered}>
                  <ProgressCircle size="small" aria-label="Loading resources" />
                </div>
              )}
              {!isLoading && isEmpty && (
                <div className={styles.centered}>
                  <Text variant="bodySmall" semanticColor="textSubdued">
                    No resources found
                  </Text>
                </div>
              )}
              {results.length > 0 && (
                <List>
                  {results.map(item => (
                    <ResourceListItem
                      key={item.name}
                      item={item}
                      onAdd={() => handleSelect(item)}
                    />
                  ))}
                  {hasNextPage && (
                    <InView
                      onChange={inView => {
                        if (inView && !isLoadingMore) {
                          loadMore();
                        }
                      }}
                     data-track-source-anchor="e49153dba7;search-resources-dialog-inner-in-view">
                      <div className={styles.loadMoreSentinel}>
                        <ProgressCircle
                          size="xsmall"
                          aria-label="Loading more"
                        />
                      </div>
                    </InView>
                  )}
                </List>
              )}
            </div>
          </div>
        </DialogComposite.Body>
      </DialogComposite.Content>
    </DialogComposite.Root>
  );
};

export const SearchResourcesDialog = (props: SearchResourcesDialogProps) => (
  <FiltersProvider
    filters={DIALOG_FILTERS}
    storageKey="search-resources-dialog"
    searchField="displayName"
    disableQueryParams
  >
    <SearchResourcesDialogInner {...props} />
  </FiltersProvider>
);
